import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Calendar } from "../../../../../../assets/icons/Calendar";
import { GraphToggle } from "../../../../../../assets/icons/GraphToggle";
import { TableToggle } from "../../../../../../assets/icons/TableToggle";
import { DateRangePicker } from "../../../../../../components/DateRangePicker";
import {
  OptionButton,
  OptionsBar,
} from "../../../../../../components/OptionsBar";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { DateFilterOptionType } from "../CustomOptionsBar";
import {
  DatepickerContainer,
  OptionsBarContainer,
  SvgCss,
} from "../CustomOptionsBar/CustomOptionsBar.styles";
import { dateFilterOptions } from "../CustomOptionsBar/dateFilterOptions";

type StepsOptionsBarProps = {
  onChange: (selectedDateFilter: DateFilterOptionType) => void;
  onTableViewChange: (enableTableView: boolean) => void;
  selectedFilterDate: DateFilterOptionType;
  showTable: boolean;
  isEmpty?: boolean;
};

export function StepsOptionsBar({
  onChange,
  onTableViewChange,
  selectedFilterDate,
  showTable,
  isEmpty = false,
}: StepsOptionsBarProps) {
  const { t } = useTranslation();

  const [calendarOpen, setCalendarOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const calendarButtonRef = useRef<HTMLDivElement>(null);

  const customDateFilterOption = "custom";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target as Node)
      ) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [calendarOpen]);

  const handleOnChangeDateWithDatePicker = (dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    if (dateRange.startDate && dateRange.endDate) {
      setCalendarOpen(false);
      onChange({
        id: customDateFilterOption,
        startDate: dayjs(dateRange.startDate),
        endDate: dayjs(dateRange.endDate).endOf("day"),
      });
    }
  };

  return (
    <OptionsBarContainer>
      <OptionsBar>
        <div
          style={{ backgroundColor: "white", borderRadius: "8px 0 0 8px" }}
          ref={calendarButtonRef}
        >
          <OptionButton
            key="steps_customDate"
            text={t("patientPage_measurementModal_optionsBar_custom")}
            icon={<Calendar />}
            selected={selectedFilterDate.id === customDateFilterOption}
            onClick={() => {
              mixpanelActions.track("User Action: CustomDateFilter");
              setCalendarOpen((prevState) => !prevState);
            }}
          />
        </div>

        {dateFilterOptions.map((dateFilterOption, index) => (
          <OptionButton
            key={`steps_${dateFilterOption.id}_${index}`}
            text={t(dateFilterOption.labelKey)}
            selected={dateFilterOption.id === selectedFilterDate.id}
            onClick={() => {
              mixpanelActions.track(
                `User Action: Changed Date Range ${dateFilterOption.id}`
              );
              const currentDate = dayjs();
              onChange({
                endDate: currentDate,
                id: dateFilterOption.id,
                startDate: dateFilterOption.value(currentDate),
              });
            }}
          />
        ))}
      </OptionsBar>

      <div>
        <OptionsBar borderRadius="16px">
          <OptionButton
            borderRadius="11px 0 0 11px"
            selected={!showTable}
            disabled={isEmpty}
            className={SvgCss()}
            text=""
            onClick={() => {
              onTableViewChange(false);
            }}
            icon={<GraphToggle />}
          />
          <OptionButton
            borderRadius="0 11px 11px 0"
            selected={showTable}
            disabled={isEmpty}
            className={SvgCss()}
            text=""
            onClick={() => {
              onTableViewChange(true);
            }}
            icon={<TableToggle />}
          />
        </OptionsBar>
      </div>

      {calendarOpen && (
        <DatepickerContainer ref={wrapperRef}>
          <DateRangePicker
            initialDateRange={{
              startDate: selectedFilterDate.startDate.toDate(),
              endDate: selectedFilterDate.endDate.endOf("day").toDate(),
            }}
            onDateChange={handleOnChangeDateWithDatePicker}
          />
        </DatepickerContainer>
      )}
    </OptionsBarContainer>
  );
}
